<template>
  <!-- Bottom tab bar for the new site on phones / tablets (the desktop has the top nav).
       The spacer keeps the end of the page from hiding behind the fixed bar. -->
  <div class="ns-tabbar-wrap">
    <div class="ns-tabbar-spacer"></div>
    <nav class="ns-tabbar" :aria-label="$t('nh_a_menu')">
      <a v-for="t in tabs" :key="t.key" class="ns-tab" :class="{ 'ns-tab--on': t.on }"
        :href="t.href" @click.prevent="t.go">
        <span class="ns-tab-icon">
          <!-- Home -->
          <svg v-if="t.key === 'home'" viewBox="0 0 24 24"><path d="M3.5 10.2 12 3.5l8.5 6.7V20a.8.8 0 0 1-.8.8h-4.9v-6.2H9.2v6.2H4.3a.8.8 0 0 1-.8-.8z" /></svg>
          <!-- Search -->
          <svg v-else-if="t.key === 'search'" viewBox="0 0 24 24"><circle cx="10.8" cy="10.8" r="6.8" /><path d="m15.8 15.8 4.7 4.7" /></svg>
          <!-- Cart (shopping bag) -->
          <svg v-else-if="t.key === 'cart'" viewBox="0 0 24 24"><path d="M5.2 7.6h13.6l-.9 12.6a.8.8 0 0 1-.8.8H6.9a.8.8 0 0 1-.8-.8z" /><path d="M8.7 7.6V6.4a3.3 3.3 0 0 1 6.6 0v1.2" /></svg>
          <!-- Rewards (heart) -->
          <svg v-else-if="t.key === 'rewards'" viewBox="0 0 24 24"><path d="M12 20.2s-7.8-4.6-7.8-10.4A4.4 4.4 0 0 1 12 7.1a4.4 4.4 0 0 1 7.8 2.7c0 5.8-7.8 10.4-7.8 10.4z" /></svg>
          <!-- Profile -->
          <svg v-else viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="10" r="3.2" /><path d="M6.3 18.4c1.2-2.2 3.3-3.4 5.7-3.4s4.5 1.2 5.7 3.4" /></svg>
          <span v-if="t.key === 'cart' && cartCount > 0" class="ns-tab-badge">{{ cartCount > 99 ? '99+' : cartCount }}</span>
        </span>
        <span class="ns-tab-label">{{ t.label }}</span>
      </a>
    </nav>
  </div>
</template>

<script>
// Every tab opens a page of the new site (Profile asks to sign in first when nobody is).
export default {
  name: 'NsTabBar',
  computed: {
    cartCount() {
      return this.$root.cartCount || 0;
    },
    tabs() {
      const p = this.$route.path;
      const loggedIn = !!this.$root.loggedIn;
      const profileTo = loggedIn ? '/web/new/profile' : '/web/new/login';
      return [
        { key: 'home',    label: this.$t('nh_tab_home'),    href: '/web/new',            on: p === '/web/new' || p === '/web/new/',
          go: () => this.open('/web/new') },
        { key: 'search',  label: this.$t('nh_tab_search'),  href: '/web/new/product',    on: p.indexOf('/web/new/product') === 0,
          go: () => this.search() },
        { key: 'cart',    label: this.$t('nh_tab_cart'),    href: '/web/new/cart',       on: p === '/web/new/cart',
          go: () => this.open('/web/new/cart') },
        { key: 'rewards', label: this.$t('nh_tab_rewards'), href: '/web/new/rewards',    on: p === '/web/new/rewards',
          go: () => this.open('/web/new/rewards') },
        { key: 'profile', label: this.$t('nh_tab_profile'), href: profileTo,             on: p === '/web/new/login' || p === '/web/new/profile' || p.indexOf('/web/new/my-orders') === 0 || p === '/web/new/addresses' || p === '/web/new/wishlist',
          go: () => this.open(profileTo) },
      ];
    },
  },
  methods: {
    open(to) {
      if (this.$route.path !== to) this.$router.push(to).catch(() => {});
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    // already on the product list: just put the cursor in its search box (inside this tap,
    // so a phone also opens the keyboard); otherwise open the list with the box focused
    search() {
      if (this.$route.path === '/web/new/product') {
        window.dispatchEvent(new Event('ns-focus-search'));
      } else {
        this.$router.push({ path: '/web/new/product', query: { focus: 'search' } }).catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.ns-tabbar-wrap { display: none; }

@media (max-width: 899px) {
  .ns-tabbar-wrap { display: block; }
  /* same height as the bar, so the last thing on the page can scroll clear of it */
  .ns-tabbar-spacer { height: calc(74px + env(safe-area-inset-bottom, 0px)); }

  .ns-tabbar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;                       /* under the menu drawer (1000) and the Live player */
    display: flex;
    justify-content: space-around;
    align-items: flex-start;
    height: calc(74px + env(safe-area-inset-bottom, 0px));
    padding: 12px 6px env(safe-area-inset-bottom, 0px);
    background: #fff;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.08);
    font-family: 'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif;
  }

  .ns-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: #8c8c8c;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
  }
  .ns-tab:hover { color: #8c8c8c; text-decoration: none; }
  .ns-tab--on, .ns-tab--on:hover { color: var(--nh-ink, #111); }

  .ns-tab-icon { position: relative; width: 28px; height: 28px; line-height: 0; }
  .ns-tab-icon svg {
    width: 28px;
    height: 28px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .ns-tab-label { font-size: 13px; line-height: 16px; white-space: nowrap; }
  .ns-tab--on .ns-tab-label { font-weight: 500; }

  .ns-tab-badge {
    position: absolute;
    top: -4px;
    right: -9px;
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    border-radius: 9px;
    background: var(--nh-ink, #111);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 17px;
    text-align: center;
  }
}
</style>
